import { useEffect, useRef, useState } from 'react';
import { api } from '../api';
// fmtDate (not a local new Date().toLocaleDateString) is deliberate:
// published_at arrives as an ISO midnight-UTC timestamp, so the naive parse
// rendered a Jul 7 entry as "Jul 6" in CST. fmtDate parses the YYYY-MM-DD
// prefix in local time.
import { colors, styles, fmtDate, useIsMobile } from './UI';

// What's New bell for the topbar. Shows an unread count badge; opening the
// panel marks everything read (server stamps whats_new_seen_at) and clears the
// badge. Entries are authored in server/services/whatsNewSeed.js. Fails silent
// — a changelog fetch error must never break the topbar.
export default function WhatsNew() {
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const wrapRef = useRef(null);
  const isMobile = useIsMobile();

  const load = async () => {
    try {
      const data = await api.whatsNew();
      setEntries(data.entries || []);
      setUnread(data.unread || 0);
    } catch {
      /* silent — never break the topbar over a changelog */
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => { load(); }, []);

  // Click-outside to close.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      setUnread(0); // optimistic — badge clears immediately
      try { await api.markWhatsNewSeen(); } catch { /* badge will reappear on next load if this failed */ }
    }
  };

  if (!loaded) return null;

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        onClick={toggle}
        aria-label="What's New"
        title="What's New"
        style={{
          position: 'relative', background: 'transparent', border: '1px solid #4B5563',
          color: '#fff', width: 38, height: 34, borderRadius: 6, fontSize: 16, cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1
        }}
      >
        🔔
        {unread > 0 && (
          <span style={{
            position: 'absolute', top: -7, right: -7, minWidth: 18, height: 18, padding: '0 4px',
            background: colors.orange, color: '#fff', borderRadius: 9, fontSize: 11, fontWeight: 700,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            border: '2px solid #111827', boxSizing: 'content-box'
          }}>
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>
      {open && (
        <div style={{
          // Desktop: anchored under the bell. Mobile: the bell sits mid-bar,
          // so a right-anchored 360px panel ran off the LEFT edge of the
          // screen — pin it to the viewport instead.
          ...(isMobile
            ? { position: 'fixed', left: 10, right: 10, top: 62, width: 'auto' }
            : { position: 'absolute', right: 0, top: '120%', width: 360, maxWidth: '90vw' }),
          maxHeight: '70vh', overflowY: 'auto', background: '#fff',
          border: `1px solid ${colors.border}`, borderRadius: 8,
          boxShadow: '0 12px 28px rgba(0,0,0,0.18)', zIndex: 40
        }}>
          <div style={{
            padding: '12px 16px', borderBottom: `1px solid ${colors.border}`,
            fontWeight: 700, fontSize: 14, color: colors.text,
            position: 'sticky', top: 0, background: '#fff'
          }}>
            What's New
          </div>
          {entries.length === 0 ? (
            <div style={{ padding: '20px 16px', color: colors.textMuted, fontSize: 13 }}>
              Nothing new right now.
            </div>
          ) : entries.map((e) => (
            <div key={e.id} style={{ padding: '12px 16px', borderBottom: `1px solid ${colors.border}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                <span style={{ fontWeight: 700, fontSize: 13, color: colors.text }}>{e.title}</span>
                <span style={{ fontSize: 11, color: colors.gray, whiteSpace: 'nowrap' }}>{fmtDate(e.published_at)}</span>
              </div>
              {e.category && (
                <span style={{
                  display: 'inline-block', marginTop: 4, padding: '1px 8px', fontSize: 10, fontWeight: 700,
                  letterSpacing: 0.5, textTransform: 'uppercase', color: colors.orangeDark,
                  background: '#FFF7ED', borderRadius: 10
                }}>
                  {e.category}
                </span>
              )}
              {e.body && (
                <div style={{ marginTop: 6, fontSize: 12.5, lineHeight: 1.5, color: '#374151' }}>{e.body}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Dashboard surface for What's New — passive display of the latest updates with
// a NEW pill on entries the user hasn't acknowledged via the bell yet. Does NOT
// clear the unread count (the topbar bell owns that explicit action). Hides
// itself entirely when there are no entries. Visible to every role.
export function WhatsNewCard({ limit = 4 }) {
  const [entries, setEntries] = useState([]);
  const [seenAt, setSeenAt] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    api.whatsNew()
      .then((data) => { if (alive) { setEntries(data.entries || []); setSeenAt(data.seen_at || null); } })
      .catch(() => {})
      .finally(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, []);

  if (!loaded || entries.length === 0) return null;

  const isNew = (e) => !seenAt || new Date(e.published_at) > new Date(seenAt);
  const shown = entries.slice(0, limit);

  return (
    <div style={{ ...styles.card, marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span style={{ fontSize: 16 }}>🔔</span>
        <h2 style={{ ...styles.h2, margin: 0 }}>What's New</h2>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {shown.map((e) => (
          <div key={e.id} style={{ borderLeft: `3px solid ${isNew(e) ? colors.orange : colors.border}`, paddingLeft: 12 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 700, fontSize: 13.5, color: colors.text }}>{e.title}</span>
              {isNew(e) && (
                <span style={{
                  padding: '0 6px', fontSize: 9, fontWeight: 800, letterSpacing: 0.5,
                  color: '#fff', background: colors.orange, borderRadius: 8
                }}>NEW</span>
              )}
              {e.category && (
                <span style={{ fontSize: 11, color: colors.orangeDark, fontWeight: 600 }}>{e.category}</span>
              )}
              <span style={{ marginLeft: 'auto', fontSize: 11, color: colors.gray, whiteSpace: 'nowrap' }}>{fmtDate(e.published_at)}</span>
            </div>
            {e.body && (
              <div style={{ marginTop: 3, fontSize: 12.5, lineHeight: 1.5, color: '#374151' }}>{e.body}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
